import { Component, h } from 'preact';
import cx from 'classnames';

import getStore from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

import { Listing, ReqState } from '../../../../../types';
import { Inquiry, ViewingList } from '../../../../../types/inquiries';
import { VlPropertySearchResponse } from '../../../../../types/inquiries/vl-property-search-response.model';
import { ALL_INQUIRY_STAGES } from '../../../../../constants/inquiries';

import { HopplerModalizer } from '../../common/hoppler-modalizer.component';
import { VlPropertySelectionPanel } from './vl-property-selection-panel';
import { VlFinalListPanel } from './vl-final-list-panel';
import { inquiryService } from '../../../../utils/inquiry.service';
import { ViewingListManager } from '../../../../core/viewing-list-manager';
import { VlPropertySearchPanel } from './vl-property-search-panel';
import { FilterViewModel } from './filter-view-model';
import { InqStageTransitionContent } from '../documents/inq-stage-transition-content.component';

interface ViewingListModalProps {
  inquiry: Inquiry;
  selectedVlKey?: string;

  onClose: () => void;
  onViewFtf?: (inquiry: Inquiry) => void;
  onScheduleViewing?: (inquiry: Inquiry) => void;
  onStageTransition?: (targetStage: string) => void;
}

interface ViewingListModalState {
  finalListVisible?: boolean;
  searchPanelVisible?: boolean;

  viewingList?: ViewingList;
  vlSaveState?: ReqState
  listingSearchResults?: VlPropertySearchResponse
}

// const TEST_INQUIRY_KEY = 'IQ26090';
// // const TEST_INQUIRY_KEY = 'IQ27714';
const store = getStore();

export class ViewingListModal extends Component<ViewingListModalProps, ViewingListModalState> {
  viewingListManager: ViewingListManager;

  async componentDidMount() {
    const { inquiry, selectedVlKey } = this.props;
    const inquiryCode = inquiry.inquiryCode;
    let viewingList: ViewingList;

    try {
      if (selectedVlKey) {
        viewingList = await inquiryService.getViewingList(inquiryCode, selectedVlKey);
      } else {
        viewingList = await inquiryService.getViewingListTemplate(inquiryCode);
      }
      this.setState({ viewingList });

      this.viewingListManager = new ViewingListManager(viewingList);
    } catch (err) {
      console.log('Failed to retrieve viewing list:', err);
    }
  }

  onTransitionCloseClick() {
    store.dispatch(InquiriesActions.loadAllStages());
    this.props.onClose();
  }

  onAddToList(listing: Listing, fromGroup: string) {
    const viewingList = this.viewingListManager.addToFinalList(listing, fromGroup);
    this.setState({ viewingList });
  }

  onRemoveFromList(listing: Listing) {
    const viewingList = this.viewingListManager.removeFromFinalList(listing);
    this.setState({ viewingList });
  }

  onListingProgressUpdate(listing: Listing, progress: string) {
    const viewingList = this.viewingListManager.updateListingProgressStatus(listing, progress);
    this.setState({ viewingList });
  }

  async onSearch(filterVm: FilterViewModel) {
    // console.log('Filter inputs:', filterVm);
    const params = this.viewingListManager.prepareFilterForPropertySearch(filterVm);
    
    try {
      const listingSearchResults = await inquiryService.searchListingsForViewingList(this.props.inquiry.inquiryCode, params);
      // console.log('Results:', listingSearchResults);

      // Close the search panel if we're on mobile
      const searchPanelVisible = window.innerWidth > 992; // lg breakpoint
      this.setState({ listingSearchResults, searchPanelVisible });
    } catch (err) {
      console.error('Failed to retrieve listings');
    }
  }

  onFinalListOpen() {
    this.setState({
      searchPanelVisible: false,
      finalListVisible: true
    });
  }

  onSearchPanelOpen() {
    this.setState({
      searchPanelVisible: true,
      finalListVisible: false
    });
  }

  onViewFtf() {
    if (this.props.onViewFtf) {
      this.props.onViewFtf(this.props.inquiry);
    }
  }

  /**
   * We currently rely on the Kumita V1 API's Schedule Viewing implementation,
   * which always sends back a value for dateViewing. To actually figure out
   * if an inquiry's viewing has been scheduled, we instead look at what stage
   * it's in.
   * 
   * @param inquiry 
   */
  hasScheduledViewing(inquiry: Inquiry) {
    let scheduled: boolean = false;

    switch (inquiry.stage) {
      case 'Viewings Scheduled':
      case 'Offer Sent':
      case 'Offer Accepted':

      // Use both in case the value is updated
      case 'For Collection':
      case 'Collections':
        scheduled = true;
        break;
    }

    return scheduled;
  }

  getStageSlug(inquiry: Inquiry) {
    // The backend label for the Collections stage is "For Collection"
    // but we label it in the frontend as "Collections".
    // Handle it as a special case:
    if (inquiry.stage === 'For Collection') {
      return 'for-collection';
    } else {
      const stage = ALL_INQUIRY_STAGES.find(stage => stage.name === inquiry.stage);
      if (stage) {
        return stage.slug;
      }
    }
  }

  onTransitionProceed() {
    const props = this.props;
    const scheduled = this.hasScheduledViewing(props.inquiry);

    if (scheduled) {
      props.onStageTransition(this.getStageSlug(props.inquiry));
    } else {
      props.onScheduleViewing(props.inquiry);
    }
  }

  onListingSliderPageChange(page: number) {
    if (page !== 5 && this.state.searchPanelVisible) {
      this.setState({ searchPanelVisible: false });
    }
  }

  async save() {
    const inquiry = this.props.inquiry;

    try {
      this.setState({ vlSaveState: ReqState.IN_PROGRESS });
      await inquiryService.saveViewingList(inquiry.inquiryCode, this.state.viewingList);
      store.dispatch(InquiriesActions.reloadSelectedInquiry(inquiry));

      this.setState({ vlSaveState: ReqState.SUCCESS });
    } catch (err) {
      this.setState({ vlSaveState: ReqState.FAILED });
      console.error('Failed to save viewing list:', err);
    }
  }

  onClose() {
    // console.log('Closed!');
    this.props.onClose();
  }

  getFinalListSection() {
    if (this.state.finalListVisible) {
      return <VlFinalListPanel
        inquiry={ this.props.inquiry }
        viewingList={ this.state.viewingList }

        onPanelClose={ () => this.setState({ finalListVisible: false }) }
        onSave={ () => this.save() }
        onScheduleViewing={ () => this.props.onScheduleViewing(this.props.inquiry) }

        onRemoveListing={ listing => this.onRemoveFromList(listing) }
        onListingProgressUpdate={ (listing, progress) => this.onListingProgressUpdate(listing, progress) }
        vlSaveState={ this.state.vlSaveState } />
    }
  }

  getSearchPanel() {
    if (this.state.searchPanelVisible) {
      return <VlPropertySearchPanel onSearch={ fvm => this.onSearch(fvm) }
        onClose={ () => this.setState({ searchPanelVisible: false }) } />;
    }
  }

  getTransitionContent(props: ViewingListModalProps, state: ViewingListModalState) {
    const inquiry = props.inquiry;
    const scheduled = this.hasScheduledViewing(inquiry);

    // READ CAREFULLY:
    // If it's scheduled, go to the stage where viewings have already been scheduled, "Viewings Scheduled"
    // Otherwise, schedule it
    const targetName = scheduled ? inquiry.stage : 'Schedule Viewing';

    return (
      <HopplerModalizer onClose={ () => this.onClose() }>
        <div className="hi-vlmodal d-flex flex-column">
          <div className="hi-vlmodal__title-bar">
            <span className="hi-vlmodal__title-label">Create Viewing List</span>
            <button className="btn btn-link hi-vlmodal__title-close" onClick={ () => this.onClose() }>
              <i className="fas fa-times" /> Close
            </button>
          </div>

          <div className="hi-vlmodal__body">
            <div className="d-flex flex-fill align-items-center justify-content-center">
              <InqStageTransitionContent targetName={ targetName }
                headingText='Successful Viewing List Creation'
                customBodyText={
                  `If you've set a schedule for your viewing, inquiry will be moved ` +
                  `under Viewings Scheduled stage. If not, it will remain under Made 1st ` +
                  `Call stage.`
                }

                onProceed={ () => this.onTransitionProceed() }
                onClose={ () => this.onTransitionCloseClick() } />
            </div>
          </div>
        </div>
      </HopplerModalizer>
    )
  }

  render(props: ViewingListModalProps, state: ViewingListModalState) {
    if (state.vlSaveState === ReqState.SUCCESS) {
      return this.getTransitionContent(props, state);
    } else if (state.viewingList) {
      return (
        <HopplerModalizer onClose={ () => this.onClose() }>
          <div className={ cx('hi-vlmodal', {
            'hi-vlmodal--final-list-visible': this.state.finalListVisible,
            'hi-vlmodal--search-panel-visible': this.state.searchPanelVisible,
          }) }>
            {/* COMPONENT: Viewing List property selection */}
            <VlPropertySelectionPanel
              viewingList={ this.state.viewingList }
              propertySearchResults={ this.state.listingSearchResults }
              onOpenFinalList={ () => this.onFinalListOpen() }
              onAddToList={ (listing, fromGroup) => this.onAddToList(listing, fromGroup) }
              onSearch={ () => this.onSearchPanelOpen() }
              onClose={ this.props.onClose }
              onViewFtf={ () => this.onViewFtf() }
              onPageChange={ page => this.onListingSliderPageChange(page) } />

            { this.getFinalListSection() }
            { this.getSearchPanel() }
          </div>
        </HopplerModalizer>
      );
    }
  }
}
